// Make or change a trade invoice, with the printed invoice previewed beside
// the form. The working copy lives in TradeSection (editor.form) so it
// survives switching tabs. Anything that needs the invoice as saved (PDF,
// email, marking it sent or paid) saves pending changes first.
import { useState } from 'react';
import { api } from '../lib/auth';
import { Button, Card, Input, Select, Textarea } from '../lib/ui';
import { invoiceDoc } from '../lib/tradeDocs';
import { savePdf } from '../lib/pdf';
import {
  STAGE, PAID_METHODS, addDays, daysBetween, formatDay, formatGBP, formToInvoice, formToPayload, invoiceFileName,
  invoiceToForm, lineAmount, newKey, sectionPriceFormat,
} from '../lib/trade';
import { InvoicePill, Label, errorText } from './TradeCommon';
import TradeDocPreview from './TradeDocPreview';
import { EmailInvoiceModal, PaidModal } from './TradeInvoiceModals';

const TERMS = [0, 7, 14, 21, 30];

function LineRow({ line, disabled, onChange, onRemove }) {
  const set = (k) => (e) => onChange({ ...line, [k]: e.target.value });
  return (
    <div className="inv-line">
      <Input type="date" value={line.date} onChange={set('date')} disabled={disabled} aria-label="Delivered" />
      <Input value={line.description} onChange={set('description')} disabled={disabled} placeholder="Item" list="trade-item-names" aria-label="Item" />
      <Input className="num" type="number" min="0" step="any" value={line.quantity} onChange={set('quantity')} disabled={disabled} aria-label="Quantity" />
      <Input value={line.unit} onChange={set('unit')} disabled={disabled} placeholder="unit" list="trade-units" aria-label="Unit" />
      <Input className="num" type="number" min="0" step="0.01" value={line.unitPrice} onChange={set('unitPrice')} disabled={disabled} aria-label="Price each" />
      <div className="amount">{formatGBP(lineAmount(line.quantity || 0, line.unitPrice || 0))}</div>
      {disabled ? <span /> : <button type="button" className="remove" title="Remove line" onClick={onRemove}>×</button>}
    </div>
  );
}

export default function TradeInvoiceEditor({
  editor, setEditor, trade, today, flash, onClose, onDuplicate, onOpenAccount, onAddAccount, onOpenSettings,
}) {
  const { form, id, dirty } = editor;
  const settings = trade.settings;
  const saved = id ? trade.invoices.find(i => i._id === id) || null : null;
  const status = saved?.status || 'draft';
  const locked = status === 'paid' || status === 'void';
  const account = trade.accounts.find(a => a._id === form.account) || null;
  const [busy, setBusy] = useState('');
  const [modal, setModal] = useState(null);       // 'email' | 'paid'
  const [picking, setPicking] = useState(false);

  const setForm = (fn) => setEditor(e => ({ ...e, form: fn(e.form), dirty: true }));
  const terms = daysBetween(form.issueDate, form.dueDate);
  const sections = trade.priceList?.sections || [];

  // ── Form edits ──────────────────────────────────────────────────────────────
  const changeAccount = (accountId) => {
    const next = trade.accounts.find(a => a._id === accountId);
    setForm(f => ({ ...f, account: accountId, dueDate: addDays(f.issueDate, next?.termsDays ?? settings?.termsDays ?? 14) }));
  };
  // Moving the invoice date keeps the same terms
  const changeIssueDate = (d) => d && setForm(f => ({ ...f, issueDate: d, dueDate: addDays(d, Math.max(0, daysBetween(f.issueDate, f.dueDate))) }));
  const changeTerms = (n) => setForm(f => ({ ...f, dueDate: addDays(f.issueDate, n) }));

  const lineDate = () => form.items[form.items.length - 1]?.date || form.issueDate;
  const addLine = (line) => setForm(f => ({ ...f, items: [...f.items, { key: newKey(), date: lineDate(), quantity: '1', ...line }] }));
  const updateLine = (key, next) => setForm(f => ({
    ...f,
    items: f.items.map(it => {
      if (it.key !== key) return it;
      // Typing a price-list item's name fills its unit and price, if they're empty
      if (next.description !== it.description && !it.unitPrice) {
        for (const s of sections) {
          const match = s.items.find(p => p.name.toLowerCase() === next.description.trim().toLowerCase());
          if (match) return { ...next, unit: next.unit || s.unit, unitPrice: Number(match.price).toFixed(2) };
        }
      }
      return next;
    }),
  }));
  const removeLine = (key) => setForm(f => ({ ...f, items: f.items.filter(it => it.key !== key) }));

  // ── Saving and actions ──────────────────────────────────────────────────────
  const save = async () => {
    if (!form.account) {
      flash('Choose who the invoice is for first', 'bad');
      return null;
    }
    setBusy('save');
    try {
      const payload = formToPayload(form);
      const { data } = saved
        ? await api.patch(`/trade/invoices/${saved._id}`, payload)
        : await api.post('/trade/invoices', payload);
      trade.apply(data);
      setEditor({ id: data.invoice._id, form: invoiceToForm(data.invoice), dirty: false });
      return data.invoice;
    } catch (e) {
      flash(errorText(e), 'bad');
      return null;
    } finally {
      setBusy('');
    }
  };

  const ensureSaved = async () => (dirty || !saved ? save() : saved);

  const after = (data, message) => {
    trade.apply(data);
    setEditor({ id: data.invoice._id, form: invoiceToForm(data.invoice), dirty: false });
    flash(message);
  };

  const setStatus = async (next, extra = {}, message = 'Saved') => {
    const inv = await ensureSaved();
    if (!inv) return false;
    setBusy(next);
    try {
      const { data } = await api.post(`/trade/invoices/${inv._id}/status`, { status: next, ...extra });
      after(data, message);
      return true;
    } catch (e) {
      flash(errorText(e), 'bad');
      return false;
    } finally {
      setBusy('');
    }
  };

  const makePdf = async () => {
    const inv = await ensureSaved();
    if (!inv) return;
    setBusy('pdf');
    try {
      const res = await savePdf(invoiceDoc(inv, settings), { fileName: invoiceFileName(inv), folder: 'TDH/Invoices' });
      if (res.path) flash(`Saved to ${res.path}`);
    } catch (e) {
      flash(errorText(e), 'bad');
    } finally {
      setBusy('');
    }
  };

  const openModal = async (which) => {
    if (await ensureSaved()) setModal(which);
  };

  const remove = async () => {
    if (!window.confirm(`Delete the draft ${saved.number}? This can't be undone.`)) return;
    try {
      await api.delete(`/trade/invoices/${saved._id}`);
      trade.dropInvoice(saved._id);
      setEditor(null);
      flash(`Deleted ${saved.number}`);
    } catch (e) {
      flash(errorText(e), 'bad');
    }
  };

  const voidIt = () => window.confirm(`Void ${saved.number}? It stays on record, marked void, and won't count as owed.`)
    && setStatus('void', {}, `${saved.number} voided`);

  // ── Preview ─────────────────────────────────────────────────────────────────
  const preview = locked && saved ? saved : formToInvoice(form, { invoice: saved, account });
  const doc = invoiceDoc(preview, settings);
  const missingBank = !(settings?.sortCode && settings?.accountNumber);
  const missingLegal = !(settings?.companyNumber && settings?.registeredOffice);
  const sortedAccounts = [...trade.accounts].sort((a, b) =>
    (a.stage === 'customer' ? 0 : 1) - (b.stage === 'customer' ? 0 : 1) || a.businessName.localeCompare(b.businessName));
  const units = [...new Set(sections.map(s => s.unit).filter(Boolean))];
  const paidHow = PAID_METHODS.find(m => m.key === saved?.paidMethod)?.label.toLowerCase();

  return (
    <div>
      <div className="inv-editor__bar">
        <Button variant="muted" onClick={onClose}>← Invoices</Button>
        <h2>{saved ? `Invoice ${saved.number}` : 'New invoice'}</h2>
        {saved && <InvoicePill invoice={saved} today={today} />}
        {dirty && <span className="muted" style={{ fontSize: 12 }}>Unsaved changes</span>}
        <span className="grow" />
        {!locked && (dirty || !saved) && (
          <Button variant="primary" onClick={save} disabled={!!busy}>{busy === 'save' ? 'Saving…' : saved ? 'Save' : 'Save draft'}</Button>
        )}
        {status !== 'void' && <Button onClick={makePdf} disabled={!!busy}>{busy === 'pdf' ? 'Making PDF…' : 'Save PDF'}</Button>}
        {!locked && <Button onClick={() => openModal('email')} disabled={!!busy}>{saved?.sentAt ? 'Email again…' : 'Email…'}</Button>}
        {status === 'draft' && saved && !dirty && (
          <Button onClick={() => setStatus('sent', {}, `${saved.number} marked as sent`)} disabled={!!busy} title="Handed over or posted, not emailed from here">Mark as sent</Button>
        )}
        {!locked && saved && <Button variant="success" onClick={() => openModal('paid')} disabled={!!busy}>Mark paid…</Button>}
        {status === 'paid' && <Button onClick={() => setStatus('sent', {}, `${saved.number} marked unpaid`)} disabled={!!busy}>Mark unpaid</Button>}
        {status === 'void' && (
          <Button onClick={() => setStatus(saved.sentAt ? 'sent' : 'draft', {}, `${saved.number} restored`)} disabled={!!busy}>Restore</Button>
        )}
      </div>

      {locked && (
        <div className={`ui-banner ${status === 'paid' ? 'ui-banner--ok' : 'ui-banner--warn'}`} style={{ marginBottom: 14 }}>
          {status === 'paid'
            ? `Paid on ${formatDay(saved.paidAt, { long: true })}${paidHow ? ` by ${paidHow}` : ''}. Mark it unpaid to change anything.`
            : 'This invoice is void. Restore it to use it again.'}
        </div>
      )}

      <div className="inv-editor">
        <div className="inv-editor__form">
          <Card>
            <div className="inv-grid2">
              <div>
                <Label>Bill to</Label>
                {trade.accounts.length ? (
                  <Select value={form.account} onChange={e => changeAccount(e.target.value)} disabled={locked}>
                    <option value="">Choose a business…</option>
                    {sortedAccounts.map(a => (
                      <option key={a._id} value={a._id}>
                        {a.businessName}{a.stage !== 'customer' ? ` (${STAGE[a.stage]?.label.toLowerCase()})` : ''}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Button onClick={onAddAccount}>+ Add a business first</Button>
                )}
                {account && (
                  <div className="inv-billto">
                    {[account.contactName, account.email].filter(Boolean).join(' · ') || 'No contact details yet'}
                    {!account.address?.line1 && <div style={{ color: 'var(--warn-fg)' }}>No address on file. Invoices should show one.</div>}
                    <div className="trade-row" style={{ marginTop: 4 }}>
                      <Button size="sm" variant="muted" onClick={() => onOpenAccount(account._id)}>Open {account.businessName}</Button>
                      {trade.accounts.length > 0 && !locked && <Button size="sm" variant="muted" onClick={onAddAccount}>+ New business</Button>}
                    </div>
                  </div>
                )}
              </div>
              <div className="inv-grid2" style={{ alignContent: 'start' }}>
                <div><Label>Invoice date</Label><Input type="date" value={form.issueDate} onChange={e => changeIssueDate(e.target.value)} disabled={locked} /></div>
                <div>
                  <Label>Payment terms</Label>
                  <Select value={TERMS.includes(terms) ? terms : 'custom'} onChange={e => e.target.value !== 'custom' && changeTerms(Number(e.target.value))} disabled={locked}>
                    {TERMS.map(n => <option key={n} value={n}>{n === 0 ? 'Due on receipt' : `${n} days`}</option>)}
                    {!TERMS.includes(terms) && <option value="custom">{terms} days</option>}
                  </Select>
                </div>
                <div><Label>Due date</Label><Input type="date" value={form.dueDate} min={form.issueDate} onChange={e => e.target.value && setForm(f => ({ ...f, dueDate: e.target.value }))} disabled={locked} /></div>
              </div>
            </div>
          </Card>

          <Card>
            <h3 style={{ margin: '0 0 10px' }}>What they had</h3>
            <div className="inv-lines">
              {form.items.length > 0 && (
                <div className="inv-line inv-line--head">
                  <span>Delivered</span><span>Item</span><span className="num">Qty</span><span>Unit</span><span className="num">Price £</span><span className="num">Amount</span><span />
                </div>
              )}
              {form.items.map(line => (
                <LineRow
                  key={line.key}
                  line={line}
                  disabled={locked}
                  onChange={(next) => updateLine(line.key, next)}
                  onRemove={() => removeLine(line.key)}
                />
              ))}
              {!form.items.length && <p className="muted" style={{ margin: '4px 0' }}>No lines yet. Add what was delivered from your price list.</p>}
            </div>
            <datalist id="trade-item-names">
              {sections.flatMap(s => s.items.map(it => <option key={`${s.name}-${it.name}`} value={it.name} />))}
            </datalist>
            <datalist id="trade-units">{units.map(u => <option key={u} value={u} />)}</datalist>

            {!locked && (
              <div className="trade-row" style={{ marginTop: 12 }}>
                <Button variant={picking ? 'dark' : 'ghost'} size="sm" onClick={() => setPicking(p => !p)}>
                  {picking ? 'Done adding' : '+ Add from price list'}
                </Button>
                <Button size="sm" onClick={() => addLine({ description: '', unit: '', unitPrice: '' })}>+ Other line</Button>
                <Button size="sm" variant="muted" onClick={() => addLine({ description: 'Delivery', quantity: '1', unit: '', unitPrice: '' })}>+ Delivery charge</Button>
              </div>
            )}
            {picking && !locked && (
              <div className="inv-picker">
                {sections.map(s => {
                  const price = sectionPriceFormat(s.items);
                  return (
                    <div key={s._id || s.name}>
                      <h4>{s.name}{s.unit ? `, per ${s.unit}` : ''}</h4>
                      {s.items.map(it => (
                        <button
                          key={it._id || it.name}
                          type="button"
                          onClick={() => addLine({ description: it.name, unit: s.unit, unitPrice: Number(it.price).toFixed(2) })}
                        >
                          <span>{it.name}</span><span>{price(it.price)}</span>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="inv-total"><span>Total</span><strong>{formatGBP(preview.total)}</strong></div>
          </Card>

          <Card>
            <Label hint="printed on the invoice">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} disabled={locked} placeholder="e.g. their PO number" />
          </Card>

          {(missingBank || missingLegal) && (
            <div className="ui-banner ui-banner--warn trade-row">
              <span style={{ flex: 1 }}>
                {missingBank && 'No bank details yet, so the invoice can\'t say how to pay. '}
                {missingLegal && 'A limited company\'s invoices need its company number and registered office.'}
              </span>
              <Button size="sm" onClick={onOpenSettings}>Invoice settings</Button>
            </div>
          )}

          {saved && (
            <div className="trade-row">
              <Button size="sm" variant="muted" onClick={() => onDuplicate(saved)}>Duplicate</Button>
              {status === 'draft' && <Button size="sm" variant="danger" onClick={remove}>Delete draft</Button>}
              {status === 'sent' && <Button size="sm" variant="danger" onClick={voidIt}>Void</Button>}
              {saved.sentTo && <span className="muted" style={{ fontSize: 12 }}>Emailed to {saved.sentTo} on {formatDay(saved.sentAt)}</span>}
            </div>
          )}
        </div>

        <div className="inv-editor__preview">
          <TradeDocPreview doc={doc} />
        </div>
      </div>

      {modal === 'email' && saved && (
        <EmailInvoiceModal
          invoice={saved}
          settings={settings}
          account={account}
          onClose={() => setModal(null)}
          onSent={(data) => { setModal(null); after(data, `Sent to ${data.invoice.sentTo}`); }}
        />
      )}
      {modal === 'paid' && saved && (
        <PaidModal
          invoice={saved}
          today={today}
          onClose={() => setModal(null)}
          onConfirm={async (extra) => {
            const ok = await setStatus('paid', extra, `${saved.number} marked paid`);
            if (ok) setModal(null);
            return ok;
          }}
        />
      )}
    </div>
  );
}
